import { Injectable, OnDestroy } from '@angular/core';

import type { ThumbnailWorkerRequest, ThumbnailWorkerResponse } from './thumbnail-worker.types';
import { createThumbnailUrl } from './photo.utils';

@Injectable({
  providedIn: 'root',
})
export class ThumbnailWorkerService implements OnDestroy {
  private worker: Worker | null = null;
  private readonly useWorker: boolean;
  private readonly pendingRequests = new Map<string, { resolve: (value: string) => void; reject: (reason: unknown) => void }>();

  constructor() {
    this.useWorker = typeof OffscreenCanvas !== 'undefined';
    if (this.useWorker) {
      this.initWorker();
    }
  }

  ngOnDestroy(): void {
    this.worker?.terminate();
    this.worker = null;
    for (const { reject } of this.pendingRequests.values()) {
      reject(new Error('Worker terminated'));
    }

    this.pendingRequests.clear();
  }

  private initWorker(): void {
    try {
      this.worker = new Worker(new URL('./thumbnail.worker.ts', import.meta.url), { type: 'module' });
      this.worker.onmessage = (event: MessageEvent<ThumbnailWorkerResponse>) => {
        const { id } = event.data;
        const pending = this.pendingRequests.get(id);
        if (!pending) return;
        this.pendingRequests.delete(id);

        if (event.data.success && event.data.blobParts) {
          const blob = new Blob([event.data.blobParts], { type: 'image/jpeg' });
          pending.resolve(URL.createObjectURL(blob));
        } else {
          pending.reject(new Error(event.data.error || 'Worker error'));
        }
      };

      this.worker.onerror = error => {
        console.warn('Thumbnail Worker error, falling back to main thread:', error);
        this.worker?.terminate();
        this.worker = null;
        for (const { reject } of this.pendingRequests.values()) {
          reject(new Error('Worker failed'));
        }

        this.pendingRequests.clear();
      };
    } catch {
      console.warn('Failed to init Thumbnail Worker, using main thread fallback');
      this.worker = null;
    }
  }

  /**
   * Создаёт миниатюру файла. Использует Web Worker если доступен, иначе — main thread.
   * @returns Blob URL миниатюры
   */
  async createThumbnail(file: File): Promise<string> {
    if (this.worker) {
      try {
        return await this.createThumbnailViaWorker(file);
      } catch {
        // Fall through to main thread
      }
    }

    return createThumbnailUrl(file);
  }

  private async createThumbnailViaWorker(file: File): Promise<string> {
    const buffer = await file.arrayBuffer();
    const workerBuffer = buffer.slice(0); // Clone because transfer detaches

    return new Promise<string>((resolve, reject) => {
      const id = crypto.randomUUID();
      this.pendingRequests.set(id, { resolve, reject });

      const msg: ThumbnailWorkerRequest = {
        id,
        type: 'createThumbnail',
        buffer: workerBuffer,
        fileName: file.name,
      };

      this.worker!.postMessage(msg, [workerBuffer]);
    });
  }
}
